const { Type } = require('@sinclair/typebox');
const { actionUtils } = require('@sstdev/lib_metadata-config');
const { merge } = require('lodash');

/**
 * Builds schema fragments for the WorkflowActionList container.
 * WorkflowActionList wraps its children (WorkflowActionPane blocks) in an 'actions' array property.
 * Each item in the actions array is an object containing the action type and any form element properties.
 */
function asDataDictionary(hNode, context, parentAccessRestrictions) {
    const { children, propertyName = 'actions' } = hNode;

    // Use hNode.securityModifier to figure out access restrictions
    const accessRestrictions = actionUtils.getNodeAccessRestrictions(hNode, context, parentAccessRestrictions);
    // If the context role or feature flags does not result in access to this (or sub) component
    // don't include any part of it in the data dictionary.
    if (accessRestrictions.hidden) return [{}, {}];

    // Process children (WorkflowActionPane blocks) to collect their action schemas
    // Each child returns { _actionSchemas: [schema] } that we'll combine with Type.Union
    let _subDataDictionary = {};
    let allActionSchemas = [];

    children?.forEach(child => {
        const [childDataDictionary = {}, childPartialType = {}] = context.extractFromChild(
            child,
            context,
            accessRestrictions
        );
        _subDataDictionary = merge(_subDataDictionary, childDataDictionary);

        // Collect action schemas from children
        if (childPartialType._actionSchemas) {
            allActionSchemas = allActionSchemas.concat(childPartialType._actionSchemas);
        }
    });

    // Build the action item schema using Union (anyOf) if we have multiple action types
    // This allows each action item to be ONE of the possible action types
    let actionItemSchema;
    if (allActionSchemas.length > 1) {
        actionItemSchema = Type.Union(allActionSchemas, {
            title: 'Action Item',
            'x-dataType': 'object'
        });
    } else if (allActionSchemas.length === 1) {
        actionItemSchema = allActionSchemas[0];
    } else {
        // Fallback: if no action panes, create a generic object schema
        actionItemSchema = Type.Object(
            {
                workflowAction: Type.Optional(
                    Type.String({
                        title: 'Workflow Action',
                        'x-dataType': 'string',
                        'x-propertyName': 'workflowAction'
                    })
                )
            },
            {
                title: 'Action Item',
                'x-dataType': 'object',
                additionalProperties: true
            }
        );
    }

    // Wrap in an actions array
    const actionsArraySchema = Type.Optional(
        Type.Array(actionItemSchema, {
            title: 'Actions',
            'x-dataType': 'array',
            'x-propertyName': propertyName
        })
    );

    // Return the actions property as a partial type definition
    // This will be merged into the parent form's schema
    const partialTypeDefinition = {
        [propertyName]: actionsArraySchema
    };

    return [_subDataDictionary, partialTypeDefinition];
}

module.exports = { asDataDictionary };
